import { resolveBrandConfig, type BrandSettings, type Theme } from '@quizee/core';
import type { LevelSettings, ResolvedConfig } from '../api/types';
import { buildStyles } from '../../../widget/src/styles';

/**
 * The stylesheet a themed widget is drawn with, for use in the editor's preview.
 *
 * This is the widget's own `buildStyles`, imported rather than copied. A preview built from a
 * second stylesheet would be a drawing of the widget, and a drawing goes stale the first time
 * somebody changes a padding: an editor would pick colours against something no reader ever sees.
 * The import is one way and type only at the widget's end, so nothing about the dashboard reaches
 * the bundle that runs on a publisher's article.
 *
 * Two things the preview cannot take from the widget:
 *
 * - `show_results`, which belongs to an interaction rather than to a brand, so the preview names a
 *   value here purely to satisfy the type. Nothing in the stylesheet reads it.
 * - The reserved height, which exists to stop a live article moving while the config loads. There
 *   is nothing to wait for here, so it is zero and the preview is as tall as its content.
 */
export function previewStyles(theme: Theme): string {
  return buildStyles({ ...theme, show_results: 'after_answer' }, 0);
}

/**
 * What a widget would look like for settings that have not been saved yet.
 *
 * This calls the same `resolveBrandConfig` the API calls, rather than merging the layers again
 * here, so a preview cannot disagree with what publishing would freeze into a version. Half typed
 * input is the one thing it cannot answer: the resolver validates, and a colour that is three
 * characters in is not a colour yet, so the last good answer stays on screen until the field
 * becomes a value again.
 */
export function resolvePreview(
  org: BrandSettings | undefined,
  brand: BrandSettings | undefined,
  fallback: ResolvedConfig,
): ResolvedConfig {
  try {
    return resolveBrandConfig(org, brand);
  } catch {
    return fallback;
  }
}

/** The settings of one level, as core wants them: absent rather than null means inherit. */
export function asSettings(level: LevelSettings | null): BrandSettings | undefined {
  if (level === null) return undefined;
  return {
    ...(level.theme === null ? {} : { theme: level.theme }),
    ...(level.consent_text === null ? {} : { consent_text: level.consent_text }),
    // Undefined and null are both "nothing set here", and only an absence resolves onwards.
    ...(level.newsletter_consent_text === null || level.newsletter_consent_text === undefined
      ? {}
      : { newsletter_consent_text: level.newsletter_consent_text }),
  };
}
